import React, { useEffect, useRef, useState } from "react";
import { ElementFactory, Question, Serializer } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

export const GEO_JSON_QUESTION_TYPE = "geo-json";
const GEO_JSON_QUESTION_ALIAS = "map";
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";

let googleMapsLoadPromise = null;

const ADDRESS_SEARCH_DEBOUNCE_MS = 500;
const ADDRESS_SEARCH_RESULT_LIMIT = 10;
const DEFAULT_ADDRESS_SEARCH_LOCATION = { lat: 51.5074, lng: -0.1278 };
const DEFAULT_ADDRESS_SEARCH_RADIUS_METERS = 50000;

export class QuestionGeoJsonModel extends Question {
  getType() {
    return GEO_JSON_QUESTION_TYPE;
  }
}

Serializer.addClass(
  GEO_JSON_QUESTION_TYPE,
  [
    { name: "liveUpdate:boolean", default: false },
    {
      name: "liveUpdateInterval:number",
      default: 60,
      minValue: 1,
    },
    { name: "liveLocationConsent:boolean", default: false, visible: false },
  ],
  () => new QuestionGeoJsonModel(""),
  "question"
);

ElementFactory.Instance.registerElement(GEO_JSON_QUESTION_TYPE, (name) => {
  return new QuestionGeoJsonModel(name);
});

ElementFactory.Instance.registerElement(GEO_JSON_QUESTION_ALIAS, (name) => {
  return new QuestionGeoJsonModel(name);
});

function createGeoJsonFeatureFromCoordinates(lat, lng) {
  return JSON.stringify({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
  });
}

function createGeoJsonFeature(position) {
  return createGeoJsonFeatureFromCoordinates(
    position.coords.latitude,
    position.coords.longitude
  );
}

function getGoogleMapsApiKey() {
  return window.GM_API_KEY || "";
}

function loadGoogleMaps() {
  if (window.google?.maps?.places?.PlacesService) {
    return Promise.resolve(window.google.maps);
  }

  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary("places").then(() => window.google.maps);
  }

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is not configured."));
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      if (window.google?.maps && !window.google.maps.places) {
        reject(new Error("Google Places could not be loaded."));
        return;
      }

      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", () =>
        reject(new Error("Google Maps could not be loaded."))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&v=3.64`;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Google Maps could not be loaded."));

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

function parseGeoJsonPoint(value) {
  try {
    const feature = typeof value === "string" ? JSON.parse(value) : value;
    const coordinates = feature?.geometry?.coordinates;

    if (
      feature?.type !== "Feature" ||
      feature?.geometry?.type !== "Point" ||
      !Array.isArray(coordinates) ||
      coordinates.length < 2
    ) {
      return null;
    }

    return {
      feature,
      position: {
        lat: Number(coordinates[1]),
        lng: Number(coordinates[0]),
      },
    };
  } catch {
    return null;
  }
}

function updateGeoJsonPoint(value, position) {
  const point = parseGeoJsonPoint(value);
  if (!point) {
    return value;
  }

  return JSON.stringify({
    ...point.feature,
    geometry: {
      ...point.feature.geometry,
      coordinates: [position.lng, position.lat],
    },
  });
}

function createAddressSearchResult(result, index) {
  const location = result?.geometry?.location;
  const label = result?.formatted_address || result?.name;
  const lat = typeof location?.lat === "function" ? location.lat() : location?.lat;
  const lng = typeof location?.lng === "function" ? location.lng() : location?.lng;

  if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    id: result?.place_id || `${label}-${index}`,
    label,
    lat,
    lng,
  };
}

function GeoJsonLocationMap({ value, onChange, isReadOnly }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const dragListenerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps()
      .then(() => {
        if (!isMounted || !mapRef.current) {
          return;
        }

        const point = parseGeoJsonPoint(value);
        if (!point) {
          setError("Selected location could not be shown on the map.");
          return;
        }

        setError(null);

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: point.position,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
        }

        mapInstanceRef.current.setCenter(point.position);

        if (!markerRef.current) {
          markerRef.current = new window.google.maps.Marker({
            map: mapInstanceRef.current,
            position: point.position,
          });
        } else {
          markerRef.current.setPosition(point.position);
        }

        markerRef.current.setDraggable(!isReadOnly);

        if (dragListenerRef.current) {
          dragListenerRef.current.remove();
          dragListenerRef.current = null;
        }

        if (!isReadOnly) {
          dragListenerRef.current = markerRef.current.addListener("dragend", () => {
            const markerPosition = markerRef.current.getPosition();
            if (!markerPosition) {
              return;
            }

            onChange(
              updateGeoJsonPoint(value, {
                lat: markerPosition.lat(),
                lng: markerPosition.lng(),
              })
            );
          });
        }
      })
      .catch((loadError) => {
        if (isMounted) {
          setError(loadError.message);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isReadOnly, onChange, value]);

  useEffect(() => {
    return () => {
      if (dragListenerRef.current) {
        dragListenerRef.current.remove();
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, []);

  return (
    <div className="geo-json-location-question__map-wrap">
      <div ref={mapRef} className="geo-json-location-question__map" />
      {error && <div className="geo-json-location-question__error">{error}</div>}
    </div>
  );
}

function getGeoJsonErrorMessage(error) {
  if (!error) {
    return "Could not get your location.";
  }

  switch (error.code) {
    case error.PERMISSION_DENIED:
      return "Location permission was denied.";
    case error.POSITION_UNAVAILABLE:
      return "Your location is currently unavailable.";
    case error.TIMEOUT:
      return "Getting your location timed out.";
    default:
      return "Could not get your location.";
  }
}

export class SurveyQuestionGeoJson extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);
    this.addressSearchTimeout = null;
    this.addressSearchRequestId = 0;
    this.liveLocationWatchId = null;
    this.liveLocationIntervalId = null;
    this.latestLivePosition = null;
    this.state = {
      isLoading: false,
      isGeocoding: false,
      error: null,
      hasLocation: Boolean(this.question.value),
      isLiveUpdating: false,
      lastLiveUpdateAt: null,
      address: "",
      addressResults: [],
    };
  }

  get question() {
    return this.questionBase;
  }

  get isReadOnly() {
    return this.question.getPropertyValue("readOnly") || this.question.isDesignMode;
  }

  get isLiveUpdateEnabled() {
    const value =
      this.question.liveUpdate ??
      this.question.getPropertyValue("liveUpdate", false);
    return value === true || String(value).toLowerCase() === "true";
  }

  get liveUpdateIntervalMs() {
    const seconds = Number(
      this.question.liveUpdateInterval ??
        this.question.getPropertyValue("liveUpdateInterval", 60)
    );
    return Math.max(1, Number.isFinite(seconds) ? seconds : 60) * 1000;
  }

  submitLatestLiveLocation = () => {
    if (!this.latestLivePosition) {
      return;
    }

    const value = createGeoJsonFeature(this.latestLivePosition);
    this.question.value = value;
    this.setState({
      error: null,
      hasLocation: true,
      isLiveUpdating: true,
      lastLiveUpdateAt: new Date(),
    });

    window.dispatchEvent(
      new CustomEvent("boomerang:live-location-update", {
        detail: { questionId: this.question.name, value },
      })
    );
  };

  startLiveLocationUpdates = () => {
    if (this.liveLocationWatchId !== null) {
      return;
    }
    if (!navigator.geolocation) {
      this.setState({ error: "Location is not supported by this browser." });
      return;
    }

    this.liveLocationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        this.latestLivePosition = position;
        this.submitLatestLiveLocation();
      },
      (error) =>
        this.setState({
          isLiveUpdating: false,
          error: getGeoJsonErrorMessage(error),
        }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    this.liveLocationIntervalId = window.setInterval(
      this.submitLatestLiveLocation,
      this.liveUpdateIntervalMs
    );
  };

  stopLiveLocationUpdates = () => {
    if (this.liveLocationWatchId !== null) {
      navigator.geolocation.clearWatch(this.liveLocationWatchId);
      this.liveLocationWatchId = null;
    }
    if (this.liveLocationIntervalId !== null) {
      window.clearInterval(this.liveLocationIntervalId);
      this.liveLocationIntervalId = null;
    }
    this.setState({ isLiveUpdating: false });
  };

  handleGetLocation = () => {
    if (!navigator.geolocation) {
      this.setState({ error: "Location is not supported by this browser." });
      return;
    }

    this.setState({ isLoading: true, error: null });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.question.value = createGeoJsonFeature(position);
        this.setState({ isLoading: false, error: null, hasLocation: true });
      },
      (error) => {
        this.setState({
          isLoading: false,
          error: getGeoJsonErrorMessage(error),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  handleMapChange = (value) => {
    this.question.value = value || null;
    this.setState({
      hasLocation: Boolean(value),
      error: null,
      addressResults: [],
    });
  };

  handleAddressChange = (event) => {
    const address = event.target.value;
    this.setState({ address, addressResults: [], error: null });
    this.queueAddressSearch(address);
  };

  componentWillUnmount() {
    this.clearAddressSearchTimeout();
    this.stopLiveLocationUpdates();
  }

  clearAddressSearchTimeout = () => {
    if (this.addressSearchTimeout !== null) {
      window.clearTimeout(this.addressSearchTimeout);
      this.addressSearchTimeout = null;
    }
  };

  queueAddressSearch = (address) => {
    this.clearAddressSearchTimeout();

    if (!address.trim()) {
      this.addressSearchRequestId += 1;
      this.setState({ isGeocoding: false, addressResults: [] });
      return;
    }

    this.addressSearchTimeout = window.setTimeout(() => {
      this.searchAddress(address, false);
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  handleAddressResultSelect = (result) => {
    if (typeof result?.lat !== "number" || typeof result?.lng !== "number") {
      this.setState({ error: "Address could not be found." });
      return;
    }

    this.question.value = createGeoJsonFeatureFromCoordinates(
      result.lat,
      result.lng
    );
    this.setState({
      error: null,
      hasLocation: true,
      address: result?.label || this.state.address,
      addressResults: [],
    });
  };

  searchAddress = (address, showEmptyError) => {
    const trimmedAddress = address.trim();

    if (!trimmedAddress) {
      this.setState({
        error: showEmptyError ? "Enter an address to search." : null,
        addressResults: [],
      });
      return;
    }

    const requestId = this.addressSearchRequestId + 1;
    this.addressSearchRequestId = requestId;
    this.setState({ isGeocoding: true, error: null, addressResults: [] });

    loadGoogleMaps()
      .then((maps) => {
        const placesContainer = document.createElement("div");
        const placesService = new maps.places.PlacesService(placesContainer);

        placesService.textSearch(
          {
            query: trimmedAddress,
            location: new maps.LatLng(
              DEFAULT_ADDRESS_SEARCH_LOCATION.lat,
              DEFAULT_ADDRESS_SEARCH_LOCATION.lng
            ),
            radius: DEFAULT_ADDRESS_SEARCH_RADIUS_METERS,
          },
          (results, status) => {
            if (requestId !== this.addressSearchRequestId) {
              return;
            }

            const addressResults = Array.from(results || [])
              .map(createAddressSearchResult)
              .filter(Boolean)
              .slice(0, ADDRESS_SEARCH_RESULT_LIMIT);

            if (
              status !== maps.places.PlacesServiceStatus.OK ||
              !addressResults.length
            ) {
              this.setState({
                isGeocoding: false,
                error: showEmptyError ? "Address could not be found." : null,
                addressResults: [],
              });
              return;
            }

            this.setState({
              isGeocoding: false,
              error: null,
              addressResults,
            });
          }
        );
      })
      .catch((loadError) => {
        if (requestId !== this.addressSearchRequestId) {
          return;
        }

        this.setState({ isGeocoding: false, error: loadError.message });
      });
  };

  handleFindAddress = () => {
    this.clearAddressSearchTimeout();
    this.searchAddress(this.state.address, true);
  };

  renderElement() {
    const {
      isLoading,
      isGeocoding,
      error,
      hasLocation,
      address,
      addressResults,
    } = this.state;

    return (
      <div className="geo-json-location-question">
        {this.isLiveUpdateEnabled && (
          <label className="geo-json-location-question__live-consent">
            <input
              type="checkbox"
              checked={Boolean(this.question.getPropertyValue("liveLocationConsent"))}
              disabled={this.isReadOnly}
              onChange={(event) =>
                this.question.setPropertyValue(
                  "liveLocationConsent",
                  event.target.checked
                )
              }
            />
            <span>
              Share live location after submitting this survey (every {this.liveUpdateIntervalMs / 1000} seconds)
            </span>
          </label>
        )}
        <div className="geo-json-location-question__hint">
          Click the button below to get your location from your browser
          automatically.
        </div>
        <button
          type="button"
          className="sd-btn sd-btn--action geo-json-location-question__button"
          disabled={this.isReadOnly || isLoading || isGeocoding}
          onClick={this.handleGetLocation}
        >
          Get your location from the browser
        </button>
        <div className="geo-json-location-question__hint">
          OR type your address below.
        </div>
        <div className="geo-json-location-question__address-row">
          <input
            type="text"
            className="geo-json-location-question__address-input"
            value={address}
            disabled={this.isReadOnly}
            onChange={this.handleAddressChange}
            placeholder="Address"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                this.handleFindAddress();
              }
            }}
          />
          <button
            type="button"
            className="sd-btn sd-btn--action geo-json-location-question__button"
            disabled={this.isReadOnly || isGeocoding}
            onClick={this.handleFindAddress}
          >
            {isGeocoding ? "Finding..." : "Find address"}
          </button>
        </div>
        {addressResults.length > 0 && (
          <div
            className="geo-json-location-question__address-results"
            data-result-count={addressResults.length}
          >
            {addressResults.map((result, index) => (
              <button
                key={result.id || index}
                type="button"
                className="geo-json-location-question__address-result"
                disabled={this.isReadOnly}
                onClick={() => this.handleAddressResultSelect(result)}
              >
                {result.label}
              </button>
            ))}
          </div>
        )}
        {hasLocation && !error && (
          <div className="geo-json-location-question__status">
            Location selected.
          </div>
        )}
        {error && <div className="geo-json-location-question__error">{error}</div>}
        {hasLocation && (
          <GeoJsonLocationMap
            value={this.question.value}
            onChange={this.handleMapChange}
            isReadOnly={this.isReadOnly}
          />
        )}
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(GEO_JSON_QUESTION_TYPE, (props) => {
  return React.createElement(SurveyQuestionGeoJson, props);
});

ReactQuestionFactory.Instance.registerQuestion(GEO_JSON_QUESTION_ALIAS, (props) => {
  return React.createElement(SurveyQuestionGeoJson, props);
});
